import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { toast } from 'react-toastify';

import { getBayValueWithoutPrefix, getSupervisionSearchStr } from '../../utils/common';
import { fetchWrapper, toUtcOffsetDisplay } from '../../helpers';
import { API_ENDPOINT } from '../../constants';
import {
  ServiceCodes,
  SessionClaimStatus,
} from '../../components/layout/billings/claims/constants';
import { asDefinedOr } from '../../components/layout/billings/claims/utils';

import { SupervisionNoteStatus } from '../../constants';
import type {
  IFamilyAttendance,
  IGroupSupervision,
  ISessionWithClaimStatus,
  ISupervisionWithClaimStatus,
  ITrainingTopic,
} from '../../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'groupSupervisions';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<GroupSupervisionState>) =>
    createExtraReducers(builder),
});

export interface GroupSupervisionState {
  list: Array<IGroupSupervision>;
  error: any;
  defaultTopics: ITrainingTopic[];
  loading: boolean;
  pdfDownloadUrls: Array<string>;
  current: IGroupSupervision | null;
  supervisionsWithClaimStatus: ISessionWithClaimStatus[];
}

type GroupSupervisionStateUpdate = {
  [Key in keyof GroupSupervisionState]: { key: Key; data: GroupSupervisionState[Key] };
}[keyof GroupSupervisionState];

function getSupervisionNoteStatus(status: string): SupervisionNoteStatus | undefined {
  switch (status) {
    case SupervisionNoteStatus.Started:
      return SupervisionNoteStatus.Started;
    case SupervisionNoteStatus.Editing:
      return SupervisionNoteStatus.Editing;
    case SupervisionNoteStatus.Completed:
      return SupervisionNoteStatus.Completed;
    default:
      return undefined;
  }
}

function createInitialState(): GroupSupervisionState {
  return {
    list: [],
    loading: false,
    defaultTopics: [],
    error: null,
    pdfDownloadUrls: [],
    current: null,
    supervisionsWithClaimStatus: [],
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
  };

  function clearState(state: GroupSupervisionState) {
    state.current = null;
    state.list = [];
    state.error = null;
    state.loading = false;
    state.pdfDownloadUrls = [];
  }

  function updateState(
    state: GroupSupervisionState,
    action: PayloadAction<GroupSupervisionStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/group_parent_supervisions`;

  return {
    getList: getList(),
    getGroupSupervision: getGroupSupervision(),
    startGroupSupervision: startGroupSupervision(),
    getGroupTrainingTopics: getGroupTrainingTopics(),
    getGPSNWithClaimStatus: getGPSNWithClaimStatus(),
    addFamilyAttendance: addFamilyAttendance(),
    deleteFamilyAttendance: deleteFamilyAttendance(),
    saveTrainingTopic: saveTrainingTopic(),
    deleteTrainingTopic: deleteTrainingTopic(),
    updateTrainingTopic: updateTrainingTopic(),
    updateFamilyAttendance: updateFamilyAttendance(),
    updateGroupSupervision: updateGroupSupervision(),
    makeGroupSupervisionEditable: makeGroupSupervisionEditable(),
    backdateGroupSupervision: backdateGroupSupervision(),
    stopGroupSupervision: stopGroupSupervision(),
    getGroupSupervisionDownloadUrls: getGroupSupervisionDownloadUrls(),
    deleteGroupSupervision: deleteGroupSupervision(),
    createTopicFamilyMapping: createTopicFamilyMapping(),
    deleteTopicFamilyMapping: deleteTopicFamilyMapping(),
    createBulkTopicFamilyMappings: createBulkTopicFamilyMappings(),
  };

  function startGroupSupervision() {
    return createAsyncThunk(
      `${name}/startGroupSupervision`,
      async ({ data, getAccessTokenSilently }: any) =>
        await fetchWrapper.post({ url: `${baseUrl}/`, getAccessTokenSilently }, data),
    );
  }

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }
        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });
        return response;
      },
    );
  }

  function getGPSNWithClaimStatus() {
    return createAsyncThunk(
      `${name}/getGPSNWithClaimStatus`,
      async ({ params, getAccessTokenSilently }: any) => {
        let baseSearch = `${baseUrl}/claim_history/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }

        const response = await fetchWrapper.get({ url: baseSearch, getAccessTokenSilently });
        return response;
      },
    );
  }

  function getGroupSupervision() {
    return createAsyncThunk(
      `${name}/getGroupSupervision`,
      async ({ groupSNId, getAccessTokenSilently }: any) =>
        await fetchWrapper.get({
          url: `${baseUrl}/${groupSNId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function updateGroupSupervision() {
    return createAsyncThunk(
      `${name}/updateGroupSupervision`,
      async ({ groupSNId, getAccessTokenSilently, data }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${groupSNId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function makeGroupSupervisionEditable() {
    return createAsyncThunk(
      `${name}/makeGroupSupervisionEditable`,
      async ({ groupSNId, getAccessTokenSilently }: any) =>
        await fetchWrapper.put({
          url: `${baseUrl}/${groupSNId}/edit/`,
          getAccessTokenSilently,
        }),
    );
  }

  function backdateGroupSupervision() {
    return createAsyncThunk(
      `${name}/backdateGroupSupervision`,
      async ({ groupSNId, getAccessTokenSilently, data }: any) =>
        await fetchWrapper.put(
          {
            url: `${baseUrl}/${groupSNId}/backdate/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function stopGroupSupervision() {
    return createAsyncThunk(
      `${name}/stopGroupSupervision`,
      async ({ groupSNId, getAccessTokenSilently, data }: any) =>
        await fetchWrapper.put(
          {
            url: `${baseUrl}/${groupSNId}/stop/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function getGroupTrainingTopics() {
    return createAsyncThunk(
      `${name}/getGroupTrainingTopics`,
      async ({ getAccessTokenSilently }: any) =>
        await fetchWrapper.get({
          url: `${baseUrl}/training_topics/`,
          getAccessTokenSilently,
        }),
    );
  }

  function saveTrainingTopic() {
    return createAsyncThunk(
      `${name}/saveTrainingTopic`,
      async ({ data, getAccessTokenSilently, groupSNId }: any) =>
        await fetchWrapper.post(
          { url: `${baseUrl}/${groupSNId}/training_topics/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function deleteTrainingTopic() {
    return createAsyncThunk(
      `${name}/deleteTrainingTopic`,
      async ({ getAccessTokenSilently, groupSNId, topicId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${groupSNId}/training_topics/${topicId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function updateTrainingTopic() {
    return createAsyncThunk(
      `${name}/updateTrainingTopic`,
      async ({ getAccessTokenSilently, groupSNId, topicId, data }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${groupSNId}/training_topics/${topicId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function createTopicFamilyMapping() {
    return createAsyncThunk(
      `${name}/createTopicFamilyMapping`,
      async ({ getAccessTokenSilently, attendanceId, groupSNId, data }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${groupSNId}/family_attendance/${attendanceId}/topics/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function deleteTopicFamilyMapping() {
    return createAsyncThunk(
      `${name}/deleteTopicFamilyMapping`,
      async ({ getAccessTokenSilently, attendanceId, groupSNId, topicId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${groupSNId}/family_attendance/${attendanceId}/topics/${topicId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function createBulkTopicFamilyMappings() {
    return createAsyncThunk(
      `${name}/createBulkTopicFamilyMappings`,
      async ({ getAccessTokenSilently, groupSNId, data }: any) =>
        await fetchWrapper.post(
          {
            url: `${baseUrl}/${groupSNId}/bulk_family_training_topics/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function addFamilyAttendance() {
    return createAsyncThunk(
      `${name}/addFamilyAttendance`,
      async ({ data, getAccessTokenSilently, groupSNId }: any) =>
        await fetchWrapper.post(
          { url: `${baseUrl}/${groupSNId}/family_attendance/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function deleteFamilyAttendance() {
    return createAsyncThunk(
      `${name}/deleteFamilyAttendance`,
      async ({ getAccessTokenSilently, groupSNId, attendanceId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${groupSNId}/family_attendance/${attendanceId}/`,
          getAccessTokenSilently,
        }),
    );
  }

  function updateFamilyAttendance() {
    return createAsyncThunk(
      `${name}/updateFamilyAttendance`,
      async ({ getAccessTokenSilently, groupSNId, attendanceId, data }: any) =>
        await fetchWrapper.patch(
          {
            url: `${baseUrl}/${groupSNId}/family_attendance/${attendanceId}/`,
            getAccessTokenSilently,
          },
          data,
        ),
    );
  }

  function getGroupSupervisionDownloadUrls() {
    return createAsyncThunk(
      `${name}/getGroupSupervisionDownloadUrls`,
      async ({ getAccessTokenSilently, groupSNId }: any) =>
        await fetchWrapper.get({
          url: `${baseUrl}/${groupSNId}/download/`,
          getAccessTokenSilently,
        }),
    );
  }

  function deleteGroupSupervision() {
    return createAsyncThunk(
      `${name}/deleteGroupSupervision`,
      async ({ getAccessTokenSilently, groupSNId }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${groupSNId}/`,
          getAccessTokenSilently,
        }),
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<GroupSupervisionState>) {
  const {
    addFamilyAttendance,
    deleteFamilyAttendance,
    getList,
    getGroupSupervision,
    getGPSNWithClaimStatus,
    getGroupTrainingTopics,
    startGroupSupervision,
    saveTrainingTopic,
    deleteTrainingTopic,
    updateTrainingTopic,
    updateFamilyAttendance,
    updateGroupSupervision,
    makeGroupSupervisionEditable,
    backdateGroupSupervision,
    stopGroupSupervision,
    createTopicFamilyMapping,
    deleteTopicFamilyMapping,
    createBulkTopicFamilyMappings,
    getGroupSupervisionDownloadUrls,
    deleteGroupSupervision,
  } = extraActions;

  builder
    //getList
    .addCase(getList.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getList.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<IGroupSupervision[]>) => {
        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getList.rejected, (state: GroupSupervisionState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getGroupSupervision()
    .addCase(getGroupSupervision.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getGroupSupervision.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<any>) => {
        state.current = action.payload;
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(getGroupSupervision.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // getGPSNWithClaimStatus()
    .addCase(getGPSNWithClaimStatus.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getGPSNWithClaimStatus.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<ISupervisionWithClaimStatus[]>) => {
        state.loading = false;
        state.supervisionsWithClaimStatus = action.payload.map(
          (supervision: ISupervisionWithClaimStatus) => {
            return {
              id: supervision.id,
              date: moment(supervision.date_start).format(),
              status:
                supervision.claim_history && supervision.claim_history?.length > 0
                  ? supervision.claim_history[0].status
                  : SessionClaimStatus.Unclaimed,
              selected: false,
              readonly: Boolean(supervision.claim_id),
              location: supervision.location,
              duration: supervision?.duration ?? 0,
              provider_name: `${supervision.bcba.first_name} ${supervision.bcba.last_name} `,
              provider_id: supervision.bcba.id,
              author_id: supervision.bcba.id,
              bay: getBayValueWithoutPrefix(supervision.family_attendance_bay),
              timeZone: supervision.utc_offset_date_start
                ? toUtcOffsetDisplay(asDefinedOr(supervision.utc_offset_date_start, 0))
                : '0:0',
              sessionStatus: getSupervisionNoteStatus(supervision.status),
              procedureCode: ServiceCodes.GroupSNProcedureCode,
            };
          },
        );
      },
    )
    .addCase(getGPSNWithClaimStatus.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // updateGroupSupervision()
    .addCase(updateGroupSupervision.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateGroupSupervision.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<any>) => {
        state.current = action.payload;
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(updateGroupSupervision.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // makeGroupSupervisionEditable()
    .addCase(makeGroupSupervisionEditable.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(makeGroupSupervisionEditable.fulfilled, (state: GroupSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('Group parent training note is now editable');
    })
    .addCase(makeGroupSupervisionEditable.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // backdateGroupSupervision()
    .addCase(backdateGroupSupervision.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(backdateGroupSupervision.fulfilled, (state: GroupSupervisionState) => {
      state.loading = false;
      state.error = null;
      toast.success('The start date of group parent training note has been updated');
    })
    .addCase(backdateGroupSupervision.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // getGroupTrainingTopics()
    .addCase(getGroupTrainingTopics.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getGroupTrainingTopics.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<ITrainingTopic[]>) => {
        state.defaultTopics = action.payload;
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(getGroupTrainingTopics.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // startGroupSupervision()
    .addCase(startGroupSupervision.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      startGroupSupervision.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<IGroupSupervision>) => {
        state.loading = false;
        state.current = action.payload;
        toast.success('Group parent training note created successfully');
      },
    )
    .addCase(startGroupSupervision.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.current = null;
      state.loading = false;
      toast.error(state.error.message);
    })
    // addFamilyAttendance()
    .addCase(addFamilyAttendance.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      addFamilyAttendance.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<IFamilyAttendance>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            family_attendance: [...(state.current.family_attendance ?? []), action.payload],
          };
        }
      },
    )
    .addCase(addFamilyAttendance.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateFamilyAttendance()
    .addCase(updateFamilyAttendance.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateFamilyAttendance.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { attendanceId: number } }>,
      ) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            family_attendance: (state.current?.family_attendance ?? []).map((item) => {
              if (String(item.id) === String(action.meta.arg.attendanceId)) {
                return action.payload;
              }
              return item;
            }),
          };
        }
      },
    )
    .addCase(updateFamilyAttendance.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createTopicFamilyMapping()
    .addCase(createTopicFamilyMapping.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createTopicFamilyMapping.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { attendanceId: number } }>,
      ) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            family_attendance: (state.current?.family_attendance ?? []).map((item) => {
              if (String(item.id) === String(action.meta.arg.attendanceId)) {
                return action.payload;
              }
              return item;
            }),
          };
        }
      },
    )
    .addCase(createTopicFamilyMapping.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // deleteTopicFamilyMapping()
    .addCase(deleteTopicFamilyMapping.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteTopicFamilyMapping.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { attendanceId: number } }>,
      ) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            family_attendance: (state.current?.family_attendance ?? []).map((item) => {
              if (String(item.id) === String(action.meta.arg.attendanceId)) {
                return action.payload;
              }
              return item;
            }),
          };
        }
      },
    )
    .addCase(deleteTopicFamilyMapping.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createBulkTopicFamilyMappings()
    .addCase(createBulkTopicFamilyMappings.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createBulkTopicFamilyMappings.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { attendanceId: number } }>,
      ) => {
        state.loading = false;
        state.current = action.payload;
      },
    )
    .addCase(
      createBulkTopicFamilyMappings.rejected,
      (state: GroupSupervisionState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )
    // deleteFamilyAttendance()
    .addCase(deleteFamilyAttendance.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteFamilyAttendance.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { attendanceId: number } }>,
      ) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            family_attendance: (state.current?.family_attendance ?? []).filter(
              (item) => String(item.id) !== String(action.meta.arg.attendanceId),
            ),
          };
        }
      },
    )
    .addCase(deleteFamilyAttendance.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // saveTrainingTopic()
    .addCase(saveTrainingTopic.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      saveTrainingTopic.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<ITrainingTopic>) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            training_topics: [...(state.current.training_topics ?? []), action.payload],
          };
        }
      },
    )
    .addCase(saveTrainingTopic.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // deleteTrainingTopic()
    .addCase(deleteTrainingTopic.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteTrainingTopic.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { topicId: number } }>,
      ) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            training_topics: (state.current.training_topics ?? []).filter(
              (item) => String(item.id) !== String(action.meta.arg.topicId),
            ),
          };
        }
      },
    )
    .addCase(deleteTrainingTopic.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateTrainingTopic()
    .addCase(updateTrainingTopic.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateTrainingTopic.fulfilled,
      (
        state: GroupSupervisionState,
        action: PayloadAction<any, string, { arg: { topicId: number } }>,
      ) => {
        state.loading = false;
        if (state.current) {
          state.current = {
            ...state.current,
            training_topics: (state.current.training_topics ?? []).map((item) => {
              if (String(item.id) === String(action.meta.arg.topicId)) {
                return action.payload;
              }
              return item;
            }),
          };
        }
      },
    )
    .addCase(updateTrainingTopic.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // stopGroupSupervision()
    .addCase(stopGroupSupervision.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(stopGroupSupervision.fulfilled, (state: GroupSupervisionState) => {
      state.loading = false;
      state.current = null;
      toast.success('Group parent training note submitted successfully');
    })
    .addCase(stopGroupSupervision.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getGroupSupervisionDownloadUrls()
    .addCase(getGroupSupervisionDownloadUrls.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getGroupSupervisionDownloadUrls.fulfilled,
      (state: GroupSupervisionState, action: PayloadAction<any>) => {
        state.pdfDownloadUrls = action.payload;
        state.loading = false;
        toast.success('Group parent training note downloaded successfully');
      },
    )
    .addCase(
      getGroupSupervisionDownloadUrls.rejected,
      (state: GroupSupervisionState, action: any) => {
        state.pdfDownloadUrls = [];
        state.error = action.error;
        state.loading = false;
        toast.error(action.error.message);
      },
    )
    .addCase(deleteGroupSupervision.pending, (state: GroupSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteGroupSupervision.fulfilled, (state: GroupSupervisionState) => {
      state.loading = false;
      state.current = null;
      toast.success('Group parent training deleted successfully');
    })
    .addCase(deleteGroupSupervision.rejected, (state: GroupSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const groupParentSupervisionActions = { ...slice.actions, ...extraActions };
export const groupParentSupervisionReducer = slice.reducer;
